import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { useColumnDrag } from "@/hooks/use-column-drag";
import { useBoardOutcome, useLeadBoard, useReturnLeadToPool, useTakeLead, type BoardCard } from "@/hooks/use-lead-board";
import type { LeadPayload } from "@/hooks/use-leads";
import { BOARD_COLUMNS, DROP_ZONES, boardMove, type BoardColumn, type BoardTarget } from "@/lib/lead-board";
import { cn } from "@/lib/utils";
import BookMeetingDialog from "../BookMeetingDialog";
import LeadBoardCard from "./LeadBoardCard";
import { CallBackDialog, EmailPlannedDialog, NotRelevantDialog } from "./LeadOutcomeDialogs";

type OpenDialog = { dialog: "email_planned" | "call_back" | "rejected"; card: BoardCard } | null;

/**
 * Leads-tavlen (VIB-3050): bookerens egen pipe ved siden av «Ett om gangen». Å dra et kort
 * er det samme som å trykke tasten på leadkortet. Avbryter du dialogen, blir kortet liggende.
 */
export default function LeadBoard() {
  const { data, isLoading, error } = useLeadBoard();
  const outcome = useBoardOutcome();
  const toPool = useReturnLeadToPool();
  const take = useTakeLead();
  const [open, setOpen] = useState<OpenDialog>(null);
  const [booking, setBooking] = useState<LeadPayload | null>(null);
  const pending = outcome.isPending || toPool.isPending || take.isPending;

  const located = useMemo(() => {
    const map = new Map<string, { card: BoardCard; column: BoardColumn }>();
    if (!data) return map;
    for (const { id } of BOARD_COLUMNS) for (const card of data[id]) map.set(card.id, { card, column: id });
    return map;
  }, [data]);

  const move = useCallback(
    (cardId: string, target: BoardTarget) => {
      const hit = located.get(cardId);
      if (!hit || pending) return;
      const result = boardMove(hit.column, target);
      if (!result) return;
      if ("message" in result) {
        toast.info(result.message);
        return;
      }
      const { action } = result;
      if (action.kind === "log") outcome.mutate({ leadId: cardId, outcome: action.outcome });
      else if (action.kind === "pool") toPool.mutate(cardId);
      else if (action.dialog === "book_meeting") take.mutate(cardId, { onSuccess: setBooking });
      else setOpen({ dialog: action.dialog, card: hit.card });
    },
    [located, pending, outcome, toPool, take],
  );
  const drag = useColumnDrag(move);

  if (isLoading) return <Skeleton className="h-96 w-full" />;
  if (error) return <p className="border border-border p-6 text-sm">{(error as Error).message}</p>;
  if (!data) return null;

  const close = () => setOpen(null);
  const counts: Record<BoardColumn, number> = {
    to_contact: data.to_contact_total,
    to_email: data.to_email.length,
    to_call: data.to_call.length,
    booked: data.booked.length,
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {BOARD_COLUMNS.map(({ id, label }) => (
          <section
            key={id}
            aria-label={`${label} (${counts[id]})`}
            {...drag.columnProps(id)}
            className={cn(
              "min-h-40 space-y-3 border-[1.5px] border-foreground/40 bg-muted/20 p-3",
              drag.overColumn === id && "border-foreground bg-muted/50",
            )}
          >
            <h3 className="font-display text-base font-semibold">
              {label} <span className="text-muted-foreground">({counts[id]})</span>
            </h3>
            {id === "to_contact" && (
              <p className="text-xs text-muted-foreground">
                Køen din · lavvann {data.queue.low_water}, høyvann {data.queue.high_water}
              </p>
            )}
            {data[id].length === 0 ? (
              <p className="text-sm text-muted-foreground">Ingen leads her.</p>
            ) : (
              // Egen rulling i søylen, så slippsonen under tavlen er synlig mens et kort dras.
              <div className="max-h-[26rem] space-y-3 overflow-y-auto pr-1">
                {data[id].map((card) => (
                  <LeadBoardCard
                    key={card.id}
                    card={card}
                    column={id}
                    dragging={drag.draggingId === card.id}
                    dragProps={drag.cardProps(card.id, id !== "booked" && !pending)}
                    onMove={(target) => move(card.id, target)}
                  />
                ))}
              </div>
            )}
            {id === "to_contact" && data.to_contact_total > data.to_contact.length && (
              <p className="text-xs text-muted-foreground">+ {data.to_contact_total - data.to_contact.length} til</p>
            )}
          </section>
        ))}
      </div>

      {/* Klebrig nederst, så slippsonen er synlig mens et kort dras, uansett skjermhøyde. */}
      <section
        aria-label="Slipp for å avslutte"
        className="sticky bottom-0 z-10 flex flex-wrap items-center gap-2 border-[1.5px] border-dashed border-foreground/40 bg-background p-3"
      >
        <span className="text-sm font-medium">Slipp for å avslutte:</span>
        {DROP_ZONES.map((z) => (
          <div
            key={z.id}
            role="group"
            aria-label={z.label}
            {...drag.columnProps(z.id)}
            className={cn(
              "border border-foreground/40 px-3 py-2 text-sm",
              drag.overColumn === z.id && "border-foreground bg-muted/50",
            )}
          >
            {z.key && <kbd className="mr-2 text-xs opacity-60">{z.key}</kbd>}
            {z.label}
          </div>
        ))}
      </section>

      <EmailPlannedDialog
        open={open?.dialog === "email_planned"}
        onOpenChange={(v) => !v && close()}
        leadName={open?.card.name ?? ""}
        pending={pending}
        onSubmit={({ sendOn, note }) => {
          if (open) outcome.mutate({ leadId: open.card.id, outcome: "email_planned", callBackAt: sendOn, note });
          close();
        }}
      />
      <CallBackDialog
        open={open?.dialog === "call_back"}
        onOpenChange={(v) => !v && close()}
        pending={pending}
        onSubmit={({ callBackAt }) => {
          if (open) outcome.mutate({ leadId: open.card.id, outcome: "call_back", callBackAt });
          close();
        }}
      />
      <NotRelevantDialog
        open={open?.dialog === "rejected"}
        onOpenChange={(v) => !v && close()}
        pending={pending}
        onSubmit={({ reason, revisitOn, note }) => {
          if (open) outcome.mutate({ leadId: open.card.id, outcome: "not_relevant", reason, revisitOn, note });
          close();
        }}
      />
      {booking && (
        <BookMeetingDialog
          open
          onOpenChange={(v) => !v && setBooking(null)}
          subject={{ kind: "lead", id: booking.lead.id, name: booking.lead.name }}
          contacts={booking.contacts}
          onBooked={() => setBooking(null)}
        />
      )}
    </div>
  );
}
